import { IStackTokens, ITextProps, mergeStyles, Stack, Text } from '@fluentui/react';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { getCompaniesStatistics, getOrdersStatistics, getReceptionPlacesStatistics } from '../../../connectors/dashboard/connectors';
import StatisticTile from './StatisticTile';

interface IStatisticTilesState {
    companiesStatistics: any;
    receptionPlacesStatistics: any;
    ordersStatistics: any;
    dayBeforeTodayDate: Date;
}

class StatisticTiles extends React.Component<WithTranslation, IStatisticTilesState> {
    constructor(props: any = {}) {
        super(props);
        
        this.state = {
            companiesStatistics: {},
            receptionPlacesStatistics: {},
            ordersStatistics: {
                local: {} as any,
                general: {} as any,
            },
            dayBeforeTodayDate: new Date(),
        };
    }

    async componentDidMount() {
        this.state.dayBeforeTodayDate.setDate(this.state.dayBeforeTodayDate.getDate() - 1);
        const companiesStatistics = await getCompaniesStatistics(this.state.dayBeforeTodayDate.toLocaleString('en-US'));
        const receptionPlacesStatistics = await getReceptionPlacesStatistics(this.state.dayBeforeTodayDate.toLocaleString('en-US'));
        const ordersStatistics = await getOrdersStatistics(this.state.dayBeforeTodayDate.toLocaleString('en-US'), []);
        const ordersStatisticsLocal = await getOrdersStatistics(this.state.dayBeforeTodayDate.toLocaleString('en-US'), ['0','1','2','3']);

        this.setState((prevState: IStatisticTilesState) => {
            return {
                ...prevState,
                companiesStatistics: companiesStatistics.data,
                receptionPlacesStatistics: receptionPlacesStatistics.data,
                ordersStatistics: { general: ordersStatistics.data, local: ordersStatisticsLocal.data }
            };
        });
    }

    public render(): JSX.Element {
        return <div>{this._basicExample()}</div>;
    }

    private _basicExample(): JSX.Element {
        const { t } = this.props;
        const { companiesStatistics, ordersStatistics, receptionPlacesStatistics } = this.state;
        const stackWithTopBorder = mergeStyles({
            marginRight: 44,
            paddingTop: 25,
            borderTop: '1px solid rgba(128,128,128, 0.25)',
        });
        const sectionStackTokens: IStackTokens = { childrenGap: 40 };

        return (
            <>
                <Stack className={stackWithTopBorder} tokens={sectionStackTokens}>
                    <Text variant={'large' as ITextProps['variant']}>{t("dashboard.statsLocalTitle", { date: this.state.dayBeforeTodayDate.toLocaleDateString('pl-PL') }) as string}</Text>

                    <Stack horizontal tokens={sectionStackTokens} style={{marginTop: 20}}>
                        <StatisticTile icon="NewFolder" value={ordersStatistics.local.newOrdersAmount}
                            backgroundColor="#ff6297" valueLabel={t("dashboard.newOrders") as string} />

                        <StatisticTile icon="NumberField" value={ordersStatistics.local.activeOrdersAmount}
                            backgroundColor="#00bcd4" valueLabel={t("dashboard.totalOrders") as string} />

                        <StatisticTile icon="Inbox" value={ordersStatistics.local.containerActiveOrdersAmount}
                            backgroundColor="#8bc34a" valueLabel={t("dashboard.containerOrders") as string} />

                        <StatisticTile icon="Weights" value={ordersStatistics.local.tonnageActiveOrdersAmount}
                            backgroundColor="#ff9800" valueLabel={t("dashboard.tonnageOrders") as string} />

                        <StatisticTile icon="Car" value={ordersStatistics.local.vehicleActiveOrdersAmount}
                            backgroundColor="#a871ff" valueLabel={t("dashboard.vehicleOrders") as string} />
                    </Stack>
                </Stack>

                <Stack className={stackWithTopBorder} tokens={sectionStackTokens} style={{marginTop: 40}}>
                    <Text variant={'large' as ITextProps['variant']}>{t("dashboard.statsOrdersTitle", { date: this.state.dayBeforeTodayDate.toLocaleDateString('pl-PL') }) as string}</Text>

                    <Stack horizontal tokens={sectionStackTokens} style={{marginTop: 20}}>
                        <StatisticTile icon="NewFolder" value={ordersStatistics.general.newOrdersAmount}
                            backgroundColor="#ff6297" valueLabel={t("dashboard.newOrders") as string} />

                        <StatisticTile icon="NumberField" value={ordersStatistics.general.activeOrdersAmount}
                            backgroundColor="#00bcd4" valueLabel={t("dashboard.totalOrders") as string} />

                        <StatisticTile icon="Inbox" value={ordersStatistics.general.containerActiveOrdersAmount}
                            backgroundColor="#8bc34a" valueLabel={t("dashboard.containerOrders") as string} />

                        <StatisticTile icon="Weights" value={ordersStatistics.general.tonnageActiveOrdersAmount}
                            backgroundColor="#ff9800" valueLabel={t("dashboard.tonnageOrders") as string} />

                        <StatisticTile icon="Car" value={ordersStatistics.general.vehicleActiveOrdersAmount}
                            backgroundColor="#a871ff" valueLabel={t("dashboard.vehicleOrders") as string} />
                    </Stack>
                </Stack>
                
                <Stack className={stackWithTopBorder} tokens={sectionStackTokens} style={{marginTop: 40}}>
                    <Text variant={'large' as ITextProps['variant']}>{t("dashboard.statsClientsTitle", { date: this.state.dayBeforeTodayDate.toLocaleDateString('pl-PL') }) as string}</Text>

                    <Stack horizontal tokens={sectionStackTokens} style={{marginTop: 20}}>
                        <StatisticTile icon="PeopleAdd" value={companiesStatistics.newClients}
                            backgroundColor="#ff6297" valueLabel={t("dashboard.newClients") as string} />

                        <StatisticTile icon="People" value={companiesStatistics.totalClientsAmount}
                            backgroundColor="#00bcd4" valueLabel={t("dashboard.totalClients") as string} />
                    </Stack>
                </Stack>

                <Stack className={stackWithTopBorder} tokens={sectionStackTokens} style={{marginTop: 40}}>
                    <Text variant={'large' as ITextProps['variant']}>{t("dashboard.statsPickupPointsTitle", { date: this.state.dayBeforeTodayDate.toLocaleDateString('pl-PL') }) as string}</Text>

                    <Stack horizontal tokens={sectionStackTokens} style={{marginTop: 20}}>
                        <StatisticTile icon="NewFolder" value={receptionPlacesStatistics.newReceptionPlacesAmount}
                            backgroundColor="#ff6297" valueLabel={t("dashboard.newPickupPoints") as string} />

                        <StatisticTile icon="Inbox" value={receptionPlacesStatistics.newContainerReceptionPlacesAmount}
                            backgroundColor="#8bc34a" valueLabel={t("dashboard.newContainerPickupPoints") as string} />

                        <StatisticTile icon="Weights" value={receptionPlacesStatistics.newTonnageReceptionPlacesAmount}
                            backgroundColor="#ff9800" valueLabel={t("dashboard.newTonnagePickupPoints") as string} />

                        <StatisticTile icon="Car" value={receptionPlacesStatistics.newVehicleReceptionPlacesAmount}
                            backgroundColor="#a871ff" valueLabel={t("dashboard.newVehiclePickupPoints") as string} />
                    </Stack>
                    <Stack horizontal tokens={sectionStackTokens} style={{marginTop: 20}}>
                        <StatisticTile icon="NewFolder" value={receptionPlacesStatistics.totalReceptionPlacesAmount}
                            backgroundColor="#ff6297" valueLabel={t("dashboard.totalPickupPoints") as string} />

                        <StatisticTile icon="Inbox" value={receptionPlacesStatistics.totalContainerReceptionPlacesAmount}
                            backgroundColor="#8bc34a" valueLabel={t("dashboard.totalContainerPickupPoints") as string} />

                        <StatisticTile icon="Weights" value={receptionPlacesStatistics.totalTonnageReceptionPlacesAmount}
                            backgroundColor="#ff9800" valueLabel={t("dashboard.totalTonnagePickupPoints") as string} />

                        <StatisticTile icon="Car" value={receptionPlacesStatistics.totalVehicleReceptionPlacesAmount}
                            backgroundColor="#a871ff" valueLabel={t("dashboard.totalVehiclePickupPoints") as string} />
                    </Stack>
                </Stack>

            </>
        );
    }
}

export default withTranslation("admin")(StatisticTiles);